<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-vehicle-work">
    <nx-report-header title="车辆作业看板">
      <el-button size="small" icon="el-icon-refresh" @click="loadData">刷新数据</el-button>
    </nx-report-header>

    <div class="kb-section train-card">
      <div class="kb-train-head">
        <h2>列车 {{ data.trainNumber }}</h2>
        <el-tag :type="statusTag" size="medium">{{ statusText }}</el-tag>
      </div>

      <div class="kb-scale-row">
        <div v-for="scale in scaleCards" :key="scale.key" class="kb-scale-card">
          <div class="kb-scale-title">
            <div class="kb-kpi-label">{{ scale.title }}</div>
            <el-tag size="mini" :type="scaleStatusType(scale.data)">{{ scaleStatusText(scale.data) }}</el-tag>
          </div>
          <div class="kb-kpi-value kb-scale-weight">{{ formatWeight(scale.data && scale.data.totalWeight) }}</div>
          <div class="kb-kpi-sub">
            已称 {{ completedCount(scale.data) }} / {{ carriageCount(scale.data) }} 节
            <span v-if="pendingCount(scale.data)">，待称 {{ pendingCount(scale.data) }} 节</span>
          </div>
          <div class="kb-kpi-sub">{{ (scale.data && scale.data.time) || '--' }}</div>
        </div>
      </div>

      <div class="kb-progress-block">
        <div class="kb-progress-head">
          <span>翻车作业进度</span>
          <span>{{ doneCount }} / {{ data.carriages.length }} 车厢</span>
        </div>
        <el-progress :percentage="progressPct" :stroke-width="16" />
        <div class="kb-kpi-sub kb-progress-time">
          开始时间：{{ data.startTime || '--' }} / 结束时间：{{ data.endTime || '--' }}
        </div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">车厢明细（共 {{ data.carriages.length }} 节）</div>
      <div class="kb-car-grid">
        <div
          v-for="c in data.carriages"
          :key="c.position"
          class="kb-car-card"
          :class="'st-' + c.status"
        >
          <div class="kb-car-title">
            <span>{{ c.carriageNumber || c.position }}</span>
            <el-tag size="mini" :type="carTagType(c)">{{ statusCar(c) }}</el-tag>
          </div>
          <div class="kb-kpi-sub kb-car-model">{{ c.model || '未录车型' }}</div>
          <div class="kb-weight-list">
            <span>毛重 {{ formatWeight(c.grossWeight) }}</span>
            <span>皮重 {{ formatWeight(c.tareWeight) }}</span>
            <span>净重 {{ formatWeight(c.actualWeight) }}</span>
            <span>标重 {{ formatWeight(c.standardWeight) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head kb-danger-head">异常记录</div>
      <div v-if="!data.exceptions || !data.exceptions.length" class="kb-floor-placeholder">
        暂无异常
      </div>
      <div v-else>
        <div v-for="(ex, i) in data.exceptions" :key="i" class="kb-ex-item">
          <div>
            <div class="kb-ex-title">车厢 {{ ex.carriageId }} · {{ ex.type }}</div>
            <div class="kb-ex-desc">{{ ex.description }}</div>
            <div class="kb-kpi-sub">{{ ex.time }}</div>
          </div>
          <el-tag :type="ex.status === 'resolved' ? 'success' : 'warning'" size="small">
            {{ ex.status === 'resolved' ? '已处理' : '待处理' }}
          </el-tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchVehicleWork } from './api'

const STATUS_MAP = {
  weighing: '重车线称重中',
  dumping: '翻卸作业中',
  leaving: '空车线称重中',
  completed: '已完成出站'
}

export default {
  name: 'KanbanVehicleWork',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      data: {
        trainNumber: '--',
        trainStatus: 'weighing',
        weighIn: null,
        weighOut: null,
        currentDumperGroup: 1,
        minutesPerGroup: '',
        carriages: [],
        exceptions: []
      }
    }
  },
  computed: {
    scaleCards() {
      return [
        { key: 'in', title: '重车线轨道衡', data: this.data.weighIn },
        { key: 'out', title: '空车线轨道衡', data: this.data.weighOut }
      ]
    },
    statusText() {
      return STATUS_MAP[this.data.trainStatus] || '待作业'
    },
    statusTag() {
      if (this.data.trainStatus === 'completed') return 'success'
      if (this.data.trainStatus === 'dumping') return 'primary'
      return 'info'
    },
    doneCount() {
      return this.data.carriages.filter((c) => c.status === 'done').length
    },
    progressPct() {
      const n = this.data.carriages.length
      if (!n) return 0
      return Math.round((this.doneCount / n) * 100)
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const query = this.$route && this.$route.query ? this.$route.query : {}
        this.data = await fetchVehicleWork({
          transportRecordId: query.transportRecordId || query.id,
          outInStockId: query.outInStockId,
          trainNumber: query.trainNumber
        })
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    formatWeight(value) {
      if (value === null || value === undefined || value === '') return '--'
      const n = Number(value)
      if (!Number.isFinite(n)) return '--'
      return `${n.toFixed(1)} 吨`
    },
    completedCount(scale) {
      return scale ? scale.completedCarriageCount || 0 : 0
    },
    carriageCount(scale) {
      return scale ? scale.carriageCount || 0 : 0
    },
    pendingCount(scale) {
      return scale ? scale.pendingCarriageCount || 0 : 0
    },
    scaleStatusText(scale) {
      return (scale && scale.statusText) || '等待称重'
    },
    scaleStatusType(scale) {
      if (scale && scale.status === 'COMPLETED') return 'success'
      if (scale && scale.status === 'PARTIAL') return 'warning'
      return 'info'
    },
    statusCar(c) {
      if (c.status === 'done') return '已完成'
      if (c.status === 'dumping') return '翻卸中'
      return '待翻卸'
    },
    carTagType(c) {
      if (c.status === 'done') return 'success'
      if (c.status === 'dumping') return 'primary'
      return 'info'
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
